import { getState, setState } from "./store";
import type { HistoryEntry } from "../types/models";

const MAX_HISTORY_SIZE = 50;

/**
 * Save current state before modification
 * Optionally include settings snapshot for generation actions
 */
export function pushHistory(classId: string, action: string, includeSettings: boolean = false): void {
    setState(s => {
        const cls = s.classes.find(c => c.id === classId);
        if (!cls) return;

        // Initialize history if missing
        if (!cls.history) {
            cls.history = { past: [], future: [] };
        }

        // Create snapshot of current state
        const entry: HistoryEntry = {
            groups: structuredClone(cls.lastGroups),
            timestamp: Date.now(),
            action
        };

        // Include settings snapshot if requested (for generation actions)
        if (includeSettings) {
            const snapshot: HistoryEntry["settingsSnapshot"] = {
                targetSize: cls.settings.targetSize,
                minSize: cls.settings.minSize,
                maxSize: cls.settings.maxSize,
                balanceSkill: cls.settings.balanceSkill
            };
            if (cls.settings.seed !== undefined) {
                snapshot.seed = cls.settings.seed;
            }
            if (cls.settings.scoringWeights !== undefined) {
                snapshot.scoringWeights = { ...cls.settings.scoringWeights };
            }
            entry.settingsSnapshot = snapshot;
        }

        // Push to past stack
        cls.history.past.push(entry);

        // Clear future (new action invalidates redo)
        cls.history.future = [];

        // Trim to max size
        if (cls.history.past.length > MAX_HISTORY_SIZE) {
            cls.history.past.shift();
        }
    });
}

/**
 * Check if undo is available
 */
export function canUndo(classId: string): boolean {
    const s = getState();
    const cls = s.classes.find(c => c.id === classId);
    return (cls?.history?.past.length ?? 0) > 0;
}

/**
 * Check if redo is available
 */
export function canRedo(classId: string): boolean {
    const s = getState();
    const cls = s.classes.find(c => c.id === classId);
    return (cls?.history?.future.length ?? 0) > 0;
}

/**
 * Restore previous state
 */
export function undo(classId: string): void {
    setState(s => {
        const cls = s.classes.find(c => c.id === classId);
        if (!cls?.history || cls.history.past.length === 0) return;

        // Save current to future
        const current: HistoryEntry = {
            groups: structuredClone(cls.lastGroups),
            timestamp: Date.now(),
            action: "current"
        };
        cls.history.future.push(current);

        // Pop from past and restore
        const prev = cls.history.past.pop()!;
        cls.lastGroups = structuredClone(prev.groups);
    });

    // Trigger re-render
    document.dispatchEvent(new CustomEvent("groups:update"));
}

/**
 * Restore next state
 */
export function redo(classId: string): void {
    setState(s => {
        const cls = s.classes.find(c => c.id === classId);
        if (!cls?.history || cls.history.future.length === 0) return;

        // Save current to past
        const current: HistoryEntry = {
            groups: structuredClone(cls.lastGroups),
            timestamp: Date.now(),
            action: "current"
        };
        cls.history.past.push(current);

        // Pop from future and restore
        const next = cls.history.future.pop()!;
        cls.lastGroups = structuredClone(next.groups);
    });

    // Trigger re-render
    document.dispatchEvent(new CustomEvent("groups:update"));
}
